// Bodentemperatur.jsx
// 'use client';

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import './wetter.css';
import '../pages/home.css';
import MyOrtMap from './MyOrtMap';
import { exportTableToPdf } from './functions'; // generischer CSV→PDF-Exporter
import BfwLogo from '../assets/bfw_logo.png';


/*   UTILS IMPORT */
import { prettyStationName } from '../utils/prettyStationName';

/* ============================ Farben & PDF-Konfig ============================ */
const depthColors = {
  5:'#4E79A7',10:'#A0CBE8',15:'#F28E2B',20:'#FFBE7D',30:'#E15759',
  40:'#FF9D9A',50:'#76B7B2',60:'#59A14F',70:'#8CD17D',80:'#B6992D'
};
const palette = ['#4E79A7','#F28E2B','#E15759','#76B7B2','#59A14F','#EDC948','#B07AA1','#FF9DA7','#9C755F','#BAB0AC'];

const PDF = {
  marginPt: 36,
  logo: { maxW: 180, maxH: 58, gap: 18 },
  tableHead: { fillColor: [68, 118, 129], textColor: [255, 255, 255] }, // #447681
};

/* ============================ Helpers ============================ */
const sniffDelimiter = (text) => {
  const head = (text || '').split(/\r?\n/).slice(0, 5).join('\n');
  const semis = (head.match(/;/g) || []).length;
  const commas = (head.match(/,/g) || []).length;
  return semis > commas ? ';' : ',';
};
const toNumber = (v) => {
  if (v == null || v === '') return null;
  const s = String(v).replace('%','').trim().replace(/\u2212/g, '-').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const canon = (s) => String(s ?? '').trim().replace(/\s+/g, ' ');
const filenameVariants = (ortRaw) => {
  const base = String(ortRaw || '').trim();
  if (!base) return [];
  const norm = base
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[Ää]/g, 'ae').replace(/[Öö]/g, 'oe').replace(/[Üü]/g, 'ue').replace(/ß/g, 'ss').trim();
  const lower = norm.toLowerCase();
  return Array.from(new Set([
    `${lower}.csv`,
    `${lower.replace(/\s+/g, '')}.csv`,
    `${lower.replace(/\s+/g, '-')}.csv`,
    `${lower.replace(/\s+/g, '_')}.csv`,
    `${norm.replace(/\b\w/g, m => m.toUpperCase()).replace(/\s+/g, '')}.csv`,
  ]));
};

/* ============================ PDF/DOM Snapshot Utils ============================ */
async function loadImageAsDataUrl(src) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}
function fitContain(imgW, imgH, maxW, maxH) {
  const ratio = imgW / imgH;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  return { w, h };
}
const snapshotNodeToCanvas = async (node, desktopWidthPx = 1200) => {
  if (!node) return null;
  const clone = node.cloneNode(true);
  Object.assign(clone.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0px',
    width: `${desktopWidthPx}px`,
    height: 'auto',
    pointerEvents: 'none',
    opacity: '1',
    transform: 'none',
    zIndex: '-1'
  });
  document.body.appendChild(clone);
  await new Promise((r) => requestAnimationFrame(r));
  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: desktopWidthPx,
  });
  document.body.removeChild(clone);
  return canvas;
};
const addDomImageMobileSafe = async ({
  pdf, node, label, nextYRef, pageW, pageH, margin, contentW,
  desktopWidthPx = 1200, widthFactor = 0.9,
}) => {
  if (!node) return;
  const canvas = await snapshotNodeToCanvas(node, desktopWidthPx);
  if (!canvas) return;
  const imgW = contentW * widthFactor;
  const naturalH = (canvas.height * imgW) / canvas.width;
  const availH = pageH - nextYRef.value - margin;
  if (naturalH > availH) { pdf.addPage(); nextYRef.value = margin; }
  const finalH = Math.min(naturalH, pageH - nextYRef.value - margin);
  const xCenter = margin + (contentW - imgW) / 2;
  if (label) {
    pdf.setFontSize(12);
    pdf.text(label, xCenter, nextYRef.value - 6);
  }
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', xCenter, nextYRef.value, imgW, finalH);
  nextYRef.value += finalH + 18;
};

/* ============================ Wide-Table Builder ============================ */
// Baut eine breite Tabelle: Datum | <depth1 cm> | <depth2 cm> | ...
const buildWideRowsForPdf = ({ filteredRows, plottedDepths }) => {
  if (!Array.isArray(filteredRows) || !filteredRows.length) return { cols: [], rows: [], depths: [] };

  const depths = (plottedDepths || []).slice().sort((a,b) => a - b);

  const byDate = new Map();
  for (const r of filteredRows) {
    if (!r?.datum || r.messtiefe == null) continue;
    if (depths.length && !depths.includes(r.messtiefe)) continue;
    const key = r.datum; // ISO yyyy-mm-dd
    if (!byDate.has(key)) byDate.set(key, { datum: key });
    byDate.get(key)[`d_${r.messtiefe}`] = r.mittel;
  }

  const rows = Array.from(byDate.values()).sort((a,b) => new Date(a.datum) - new Date(b.datum));

  const cols = [
    { header: 'Datum', dataKey: 'datum' },
    ...depths.map(d => ({ header: `${d} cm`, dataKey: `d_${d}` }))
  ];
  return { cols, rows, depths };
};
const formatAtNumber = (n) =>
  (n == null || Number.isNaN(n))
    ? ''
    : n.toLocaleString('de-AT', { maximumFractionDigits: 2 });

/* ============================ Komponente ============================ */
const Bodentemperatur = ({ ort, untersuchungsgebiet, versuchsflaecheName }) => {
  const [baseRows, setBaseRows] = useState([]); // bereinigte Felder
  const [gebiet, setGebiet] = useState(untersuchungsgebiet || '');
  const [flaeche, setFlaeche] = useState(versuchsflaecheName || '');
  const [selectedDepths, setSelectedDepths] = useState([]);
  const [showAll, setShowAll] = useState(false);

  // komplette CSV (ungefiltert)
  const [csvAllRows, setCsvAllRows] = useState([]);
  const [csvAllHeaders, setCsvAllHeaders] = useState([]);

  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [pageIndex, setPageIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [usedUrl, setUsedUrl] = useState('');
  const [testedUrls, setTestedUrls] = useState([]);
  const abortRef = useRef(null);

  // Logo einmal laden & cachen
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  useEffect(() => {
    let mounted = true;
    loadImageAsDataUrl(BfwLogo).then(d => { if (mounted) setLogoDataUrl(d); }).catch(()=>{});
    return () => { mounted = false; };
  }, []);

  // Refs für Screenshots
  const mapRef = useRef(null);
  const chartRef = useRef(null);

  /* ============================ Laden ============================ */
  const fetchCsvText = useCallback(async (signal) => {
    const base = 'https://geo.bfw.ac.at/downloads/waldoekodaten/bodentemperatur/';
    const names = filenameVariants(ort);
    const tried = [];
    for (const name of names) {
      const url = base + name;
      tried.push(url);
      try {
        const res = await fetch(encodeURI(url), { cache:'no-store', mode:'cors', credentials:'omit', signal });
        if (!res.ok) continue;
        const text = (await res.text()).replace(/^\uFEFF/, '');
        return { url, text, tried };
      } catch {/* weiter */}
    }
    return { url:null, text:null, tried };
  }, [ort]);

  const parseCsvForChart = (text) => {
    const delimiter = sniffDelimiter(text);
    const { data } = Papa.parse(text, { header:true, skipEmptyLines:true, delimiter });
    return (data || [])
      .map(r => {
        if (!r) return null;
        const g = canon(r.untersuchungsgebiet);
        const f = canon(r.versuchsflaeche_name);
        const d = r.datum ? String(r.datum).trim() : '';
        const t = toNumber(r.messtiefe);
        const m = toNumber(r.mittel);
        if (!d || t == null) return null;
        return { untersuchungsgebiet: g, versuchsflaeche_name: f, datum: d, messtiefe: t, mittel: m };
      })
      .filter(Boolean);
  };

  const loadCsv = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      setLoading(true);
      setErrorMsg('');
      setUsedUrl('');
      setTestedUrls([]);
      setBaseRows([]);
      setCsvAllRows([]);
      setCsvAllHeaders([]);
      setPageIndex(0);

      const { url, text, tried } = await fetchCsvText(ac.signal);
      setTestedUrls(tried || []);
      if (!text) {
        setErrorMsg(`Keine CSV gefunden. Probiert: ${(tried||[]).join(' | ')}`);
        return;
      }

      const rows = parseCsvForChart(text);
      setUsedUrl(url || '');
      setBaseRows(rows);

      const delimiter = sniffDelimiter(text);
      const parsedAll = Papa.parse(text, { header: true, skipEmptyLines: true, delimiter });
      const headers = (parsedAll.meta?.fields || []).filter(Boolean);
      setCsvAllHeaders(headers.length ? headers : Object.keys(parsedAll.data?.[0] || {}));
      setCsvAllRows(Array.isArray(parsedAll.data) ? parsedAll.data : []);

      // Defaults
      if (!gebiet || !rows.some(r => r.untersuchungsgebiet === canon(gebiet))) {
        const firstGebiet = rows[0]?.untersuchungsgebiet || '';
        setGebiet(firstGebiet);
      }
      if (!flaeche) {
        const firstFlaeche = rows.find(r => r.untersuchungsgebiet === (rows[0]?.untersuchungsgebiet || ''))?.versuchsflaeche_name || '';
        setFlaeche(firstFlaeche);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') setErrorMsg(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [fetchCsvText, gebiet, flaeche]);

  useEffect(() => { loadCsv(); return () => abortRef.current?.abort(); }, [loadCsv]);

  /* ============================ Areas & Auswahl ============================ */
  const areas = useMemo(() => {
    const key = (g, f) => `${g}||${f}`;
    const map = new Map();
    for (const r of baseRows) {
      if (!r.untersuchungsgebiet || !r.versuchsflaeche_name) continue;
      const k = key(r.untersuchungsgebiet, r.versuchsflaeche_name);
      if (!map.has(k)) map.set(k, { gebiet: r.untersuchungsgebiet, flaeche: r.versuchsflaeche_name });
    }
    return Array.from(map.values()).sort((a,b) =>
      a.gebiet.localeCompare(b.gebiet) || a.flaeche.localeCompare(b.flaeche)
    );
  }, [baseRows]);

  const flaechenOptions = useMemo(
    () => areas.filter(a => a.gebiet === canon(gebiet)).map(a => a.flaeche),
    [areas, gebiet]
  );

  const safeFlaeche = useMemo(() => {
    const cur = canon(flaeche);
    const hit = flaechenOptions.find(f => canon(f) === cur);
    return hit ?? (flaechenOptions[0] ?? '');
  }, [flaechenOptions, flaeche]);

  useEffect(() => {
    if (safeFlaeche && canon(safeFlaeche) !== canon(flaeche)) {
      setFlaeche(safeFlaeche);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeFlaeche]);

  const handleChangeSelection = (nextGebiet, nextFlaeche) => {
    setGebiet(nextGebiet);
    const opts = areas.filter(a => a.gebiet === canon(nextGebiet)).map(a => a.flaeche);
    setFlaeche(nextGebiet ? (nextFlaeche || opts[0] || '') : '');
    setPageIndex(0);
  };

  /* ============================ Filter & Ableitungen ============================ */
  const filteredRows = useMemo(() => {
    if (!baseRows.length) return [];
    const g = canon(gebiet);
    const f = canon(safeFlaeche || flaeche);
    const hasSelection = g && f;
    return hasSelection
      ? baseRows.filter(r => r.untersuchungsgebiet === g && r.versuchsflaeche_name === f)
      : baseRows;
  }, [baseRows, gebiet, flaeche, safeFlaeche]);

  const availableDepths = useMemo(() => {
    const s = new Set();
    for (const r of filteredRows) s.add(r.messtiefe);
    return Array.from(s).sort((a,b) => a - b);
  }, [filteredRows]);

  useEffect(() => {
    if (!availableDepths.length) { setSelectedDepths([]); return; }
    if (!selectedDepths.length) {
      setSelectedDepths([availableDepths[0]]);
      setShowAll(false);
      return;
    }
    const still = selectedDepths.filter(d => availableDepths.includes(d));
    if (!still.length) {
      setSelectedDepths([availableDepths[0]]);
      setShowAll(false);
    } else if (still.length !== selectedDepths.length) {
      setSelectedDepths(still);
    }
  }, [availableDepths, selectedDepths]);

  const wideData = useMemo(() => {
    const byDate = new Map();
    for (const r of filteredRows) {
      const key = r.datum;
      if (!byDate.has(key)) byDate.set(key, { datum: r.datum });
      byDate.get(key)[`bodentemperatur${r.messtiefe}`] = r.mittel;
    }
    return Array.from(byDate.values()).sort((a,b) => new Date(a.datum) - new Date(b.datum));
  }, [filteredRows]);

  const plottedDepths = useMemo(() => {
    return showAll ? availableDepths.slice() : (selectedDepths[0] ? [selectedDepths[0]] : []);
  }, [showAll, selectedDepths, availableDepths]);

  // Paginierung
  const entriesPerPageSafe = entriesPerPage === 'ALL' ? wideData.length : entriesPerPage;
  const pageCount = useMemo(
    () => (entriesPerPageSafe > 0 ? Math.ceil(wideData.length / entriesPerPageSafe) : 0),
    [wideData.length, entriesPerPageSafe]
  );
  const displayedData = useMemo(() => {
    const start = pageIndex * entriesPerPageSafe;
    return wideData.slice(start, start + entriesPerPageSafe);
  }, [wideData, pageIndex, entriesPerPageSafe]);

  /* ============================ CSV-Filter-Spalten (für Komplett-Export) ============================ */
  const getCsvFilterColumns = useCallback(() => {
    const findCol = (name) =>
      csvAllHeaders.find(h => String(h).toLowerCase() === name) || null;
    return {
      colGebiet:  findCol('untersuchungsgebiet'),
      colFlaeche: findCol('versuchsflaeche_name'),
      colTiefe:   findCol('messtiefe'),
    };
  }, [csvAllHeaders]);

  const getCsvRowsFilteredBySelection = useCallback(() => {
    const { colGebiet, colFlaeche, colTiefe } = getCsvFilterColumns();
    if (!colGebiet || !colFlaeche) return [];
    const gSel = canon(gebiet);
    const fSel = canon(safeFlaeche || flaeche);

    let rows = csvAllRows.filter(r =>
      canon(r?.[colGebiet]) === gSel && canon(r?.[colFlaeche]) === fSel
    );

    if (colTiefe) {
      const wanted = new Set(plottedDepths || []);
      if (wanted.size > 0) {
        rows = rows.filter(r => wanted.has(toNumber(r?.[colTiefe])));
      }
    }
    return rows;
  }, [csvAllRows, gebiet, flaeche, safeFlaeche, getCsvFilterColumns, plottedDepths]);

  /* ============================ PDF Download: Report (Logo → Meta → Map → Chart → Wide-Table) ============================ */
  const handleFullReportPdf = useCallback(async () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF.marginPt;
    const contentW = pageW - margin * 2;

    // Logo & Titel
    let titleTop = margin;
    if (logoDataUrl) {
      const props = pdf.getImageProperties(logoDataUrl);
      const { w, h } = fitContain(props.width, props.height, PDF.logo.maxW, PDF.logo.maxH);
      pdf.addImage(logoDataUrl, 'PNG', margin, margin - 2, w, h);
      titleTop = margin - 2 + h + PDF.logo.gap;
    }

    pdf.setFontSize(16);
    pdf.text('Bodentemperatur', margin, titleTop);
    pdf.setFontSize(11);
    const meta = [
      `Gebiet: ${prettyStationName(ort) || '-'}`,
      `Fläche: ${canon(safeFlaeche) || '-'}`,
      plottedDepths.length ? `Messtiefe(n): ${plottedDepths.join(', ')} cm` : ''
    ].filter(Boolean).join('  ·  ');
    pdf.text(meta, margin, titleTop + 18);

    const nextYRef = { value: titleTop + 36 };

    // Karte + Diagramm
    await addDomImageMobileSafe({
      pdf, node: mapRef.current, label: '',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 1.25,
    });
    await addDomImageMobileSafe({
      pdf, node: chartRef.current, label: 'Grafische Darstellung – Diagramm',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 0.9,
    });

    // WIDE TABLE: Datum | <depth1 cm> | <depth2 cm> | ...
    const { cols, rows, depths } = buildWideRowsForPdf({ filteredRows, plottedDepths });

    if (nextYRef.value > pageH - 120) { pdf.addPage(); nextYRef.value = margin; }

    if (rows.length) {
      // Ausrichtung: Datum links, Tiefe-Spalten rechts
      const columnStyles = { datum: { halign: 'left', cellWidth: 60 } };
      for (const d of depths) columnStyles[`d_${d}`] = { halign: 'left' };

      pdf.autoTable({
        columns: cols,
        body: rows.map(r => {
          const out = { datum: r.datum ? new Date(r.datum).toLocaleDateString('de-AT') : '' };
          for (const d of depths) out[`d_${d}`] = r[`d_${d}`] ?? '';
          return out;
        }),
        startY: nextYRef.value,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { ...PDF.tableHead, fontStyle: 'bold', halign: 'left' },
        columnStyles,
        margin: { left: margin, right: margin },
        didParseCell: (data) => {
          if (String(data.column.dataKey).startsWith('d_') && data.cell.raw !== '' && data.cell.raw != null) {
            const num = Number(data.cell.raw);
            if (!Number.isNaN(num)) data.cell.text = [formatAtNumber(num)];
          }
        },
        didDrawPage: function () {
          const pageCount = pdf.internal.getNumberOfPages();
          const { pageNumber } = pdf.internal.getCurrentPageInfo();
          pdf.setFontSize(9);
          pdf.text(`Seite ${pageNumber} / ${pageCount}`, pageW - margin - 90, pageH - margin / 2);
        },
      });
    } else {
      pdf.setFontSize(11);
      pdf.text('Keine Daten für die aktuelle Auswahl/Messtiefen gefunden.', margin, nextYRef.value);
    }

    pdf.save(`Bodentemperatur_Report_${ort || 'daten'}.pdf`);
  }, [logoDataUrl, gebiet, safeFlaeche, plottedDepths, filteredRows, ort]);

  /* ============================ CSV → PDF (ALLE Spalten/Zeilen) ============================ */
  const handleCsvToPdfAll = useCallback(() => {
    if (!csvAllRows.length || !csvAllHeaders.length) return;
    const columns = csvAllHeaders.map((h) => ({ header: h, dataKey: h }));
    const rows = csvAllRows.map((r) => {
      const o = {}; csvAllHeaders.forEach((h) => { o[h] = r?.[h] ?? ''; }); return o;
    });

    exportTableToPdf({
      rows,
      columns,
      title: `CSV Export – ${ort || ''} (${canon(gebiet)} / ${canon(safeFlaeche)}) – ALLE Spalten`,
      filename: `Bodentemperatur_CSV_alle_${ort || 'daten'}.pdf`,
    });
  }, [csvAllRows, csvAllHeaders, ort, gebiet, safeFlaeche]);

  /* ============================ Render ============================ */
  return (
    <div className="wetter-container" id="pdfContent">
      <h2><strong>{prettyStationName(ort)}</strong> </h2>
      <div ref={mapRef} className="home-map"><MyOrtMap ort={ort} /></div>

      {/* Auswahl / Aktionen */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', margin:'8px 0' }}>
        <label style={{ fontSize:14}}>GEBIET: {prettyStationName(ort)}</label>
        <br />
        <label style={{ fontSize:14}}>
          FLÄCHE:&nbsp;
          <select
            className="dropdown"
            value={safeFlaeche}
            onChange={(e) => handleChangeSelection(gebiet, e.target.value)}
          >
            {flaechenOptions.map((f, idx) => (
              <option key={`${gebiet}-${f}-${idx}`} value={f}>{f}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Tiefen */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8, marginTop:40 }}>
        {availableDepths.map((d, i) => {
          const color = depthColors[d] || palette[i % palette.length];
          const isActive = !showAll && selectedDepths.length === 1 && selectedDepths[0] === d;
          return (
            <button
              key={d}
              className={`param-button ${isActive ? 'active-button' : ''}`}
              onClick={() => { setShowAll(false); setSelectedDepths([d]); }}
              title={`Messtiefe ${d} cm`}
              style={{ borderColor: color }}
              disabled={loading}
            >
              {d} cm
            </button>
          );
        })}

        <button
          className={`param-button ${showAll ? 'active-button' : ''}`}
          onClick={() => { setShowAll(true); setSelectedDepths(availableDepths); }}
          disabled={loading || availableDepths.length === 0}
          title="Alle Tiefen anzeigen"
        >
          Alle
        </button>
      </div>

      {/* Diagramm */}
      <h3>Grafische Darstellung</h3>
      <div ref={chartRef} className="linechart-div" style={{ width:'100%', height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={wideData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="datum"
              tick={{ fontSize: 11 }}
              tickFormatter={(d) => (d ? new Date(d).toLocaleDateString('de-AT') : '')}
            />
            <YAxis />
            <Tooltip labelFormatter={(d) => (d ? new Date(d).toLocaleDateString('de-AT') : '')} />
            <Legend />
            {plottedDepths.map((d, i) => {
              const key = `bodentemperatur${d}`;
              const color = depthColors[d] || palette[i % palette.length];
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={`Messtiefe ${d} cm`}
                  stroke={color}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                  strokeWidth={2}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <br />
      <div>
        {usedUrl && (
          <>
            <a
              href={usedUrl}
              target="_blank"
              rel="noreferrer"
              className="param-button active-button"
              style={{ marginBottom: 0, marginRight: 8, textDecoration: 'none' }}
            >
              CSV Download
            </a>

            {/* CSV → PDF: ALLE Daten aus der CSV (alle Spalten) */}
            <button
              onClick={handleCsvToPdfAll}
              className="param-button active-button"
              disabled={loading || !csvAllRows.length}
              style={{ marginBottom: 0, marginRight: 8 }}
            >
              CSV → PDF Download
            </button>
          </>
        )}
      </div>
      <br />

      <h3>Messwerte</h3>

      <div className="entries-dropdown">
        <label>
          <strong>Einträge pro Seite:</strong>{' '}
          <select
            className="dropdown"
            value={entriesPerPage === wideData.length ? 'ALL' : String(entriesPerPage)}
            onChange={(e) => {
              const val = e.target.value === 'ALL' ? wideData.length : parseInt(e.target.value, 10);
              setEntriesPerPage(val);
              setPageIndex(0);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value="ALL">Alle</option>
          </select>
        </label>
      </div>


       {/* Tabelle */}
       <div className="table-wrapper">
          <table className="data-table responsive-table" role="table" aria-label="Messwerte Tabelle">
            <thead>
              <tr>
                <th style={{width:40}}>Nr.</th>
                <th style={{width:100}}>Datum</th>
                {plottedDepths.map((d) => (
                  <th key={d}  style={{width:100}}>Messtiefe {d} cm</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedData.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td>{pageIndex * (entriesPerPage === 'ALL' ? wideData.length : entriesPerPage) + idx + 1}</td>
                  <td>{row.datum ? new Date(row.datum).toLocaleDateString('de-AT') : '-'}</td>
                  {plottedDepths.map((d) => (
                    <td key={d}>{row[`bodentemperatur${d}`] ?? '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
        </table>
       </div>
      

      {pageCount > 1 && (
        <div className="page-buttons">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button key={i} onClick={() => setPageIndex(i)} className={pageIndex === i ? 'active' : ''}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="info-banner">Lade Daten…</div>}
      {!!errorMsg && (
        <div className="error-banner">
          <div><strong>Fehler:</strong> {errorMsg}</div>
          {testedUrls.length > 0 && (
            <div style={{ marginTop:6, fontSize:12 }}>
              <strong>Getestete URLs:</strong> {testedUrls.join(' · ')}
            </div>
          )}
        </div>
      )}
      {!loading && !errorMsg && !wideData.length && <div className="info-banner">Keine Daten vorhanden.</div>}

      <br />
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <button
          className="param-button"
          onClick={handleFullReportPdf}
          disabled={loading || (!csvAllRows.length && !wideData.length)}
          title="als PDF download"
        >
          PDF Download
        </button>
      </div>

      <br />
      <p><small>* „PDF Download (Report)“ erzeugt eine Tabelle mit einer Spalte pro gewählter Messtiefe (aufsteigend), Zeilen nach Datum (aufsteigend). „CSV → PDF Download“ exportiert die komplette, ungefilterte CSV.</small></p>
    </div>
  );
};

export default Bodentemperatur;
